import Papa from 'papaparse';
import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import TriangulationService from '../../../../services/TriangulationService';
import hasDesiredKeysAndValues from '../../../../utils/hasDesiredKeysAndValues';
import hasDuplicates from '../../../../utils/hasDuplicatedValueAtArray';
import onlyNumbers from '../../../../utils/onlyNumbers';
import toUTF8 from '../../../../utils/toUTF8';
import { CsvParsedType, JsonFromCsvType } from '../../types';

interface UseSubmitFileInterface {
  getTriangulationData: () => void;
}

export default function useSubmitFile({ getTriangulationData }: UseSubmitFileInterface) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvParsed, setCsvParsed] = useState<CsvParsedType[]>([]);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode } = useGetGeneralAppData();

  async function handleFileUpload(e: ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (files) {
      setCsvFileName(files[0].name);
      const utf8File = await toUTF8(files[0]);

      Papa.parse(utf8File, {
        header: true,
        complete: (results: { data: JsonFromCsvType }) => {
          const resultArray = results.data;
          const filteredArray = resultArray.filter((index) => !!index.CODIGO_LOC);
          const desiredHeaders = ['CPF_FUNC', 'MATRICULA_FUNC', 'NOME_FUNC', 'LAT_FUNC', 'LONG_FUNC', 'CEP_FUNC', 'LOGRADOURO_FUNC', 'NUMERO_FUNC', 'BAIRRO_FUNC', 'CIDADE_FUNC', 'UF_FUNC', 'COD LOCAL ATUAL', 'DESCRICAO_LOC ATUAL', 'CEP_LOC ATUAL', 'LOGRADOURO_LOC ATUAL', 'NUMERO_LOC ATUAL', 'BAIRRO_LOC ATUAL', 'CIDADE_LOC ATUAL', 'UF_LOC ATUAL', 'CODIGO_LOC', 'DESCRICAO_LOC', 'LAT_LOC', 'LONG_LOC', 'CEP_LOC', 'LOGRADOURO_LOC', 'NUMERO_LOC', 'BAIRRO_LOC', 'CIDADE_LOC', 'UF_LOC', 'VALOR ANTES', 'VALOR DEPOIS', 'SIND', 'CARGO', 'RAIO',];
          if (!hasDesiredKeysAndValues(filteredArray, desiredHeaders) || filteredArray.length === 0) {
            toast.error('O arquivo não contém os cabeçalhos desejados, ou algum valor destes não está preenchido');
            return;
          }
          setCsvParsed(filteredArray.map((index) => ({
            codEmpresa: companyCode,
            dataImpArquivo: new Date(),
            cpf: index.CPF_FUNC.slice(0, 18),
            matricula: index.MATRICULA_FUNC.slice(0, 50),
            nome: index.NOME_FUNC.slice(0, 50),
            funcLat: Number(index.LAT_FUNC?.replaceAll('_', '.').replaceAll(',', '.')),
            funcLong: Number(index.LONG_FUNC?.replaceAll('_', '.').replaceAll(',', '.')),
            funcCep: index.CEP_FUNC.slice(0, 9),
            funcLogradouro: index.LOGRADOURO_FUNC.slice(0, 50),
            funcNumLogra: Number(onlyNumbers(index.NUMERO_FUNC)),
            funcBairro: index.BAIRRO_FUNC.slice(0, 25),
            funcCidade: index.CIDADE_FUNC.slice(0, 30),
            funcUF: index.UF_FUNC.slice(0, 2),
            LTACod: Number(index['COD LOCAL ATUAL']),
            LTADescr: index['DESCRICAO_LOC ATUAL'].slice(0, 40),
            LTALat: 0,
            LTALong: 0,
            LTACep: index['CEP_LOC ATUAL'].slice(0, 9),
            LTALogradouro: index['LOGRADOURO_LOC ATUAL'].slice(0, 50),
            LTANumLogra: Number(onlyNumbers(index['NUMERO_LOC ATUAL'])),
            LTABairro: index['BAIRRO_LOC ATUAL'].slice(0, 25),
            LTACidade: index['CIDADE_LOC ATUAL'].slice(0, 30),
            LTAUF: index['UF_LOC ATUAL'].slice(0, 2),
            LTICod: Number(index.CODIGO_LOC),
            LTIDescr: index.DESCRICAO_LOC.slice(0, 40),
            LTILat: Number(index.LAT_LOC?.replaceAll('_', '.').replaceAll(',', '.')),
            LTILong: Number(index.LONG_LOC?.replaceAll('_', '.').replaceAll(',', '.')),
            LTICep: index.CEP_LOC.slice(0, 9),
            LTILogradouro: index.LOGRADOURO_LOC.slice(0, 50),
            LTINumLogra: Number(onlyNumbers(index.NUMERO_LOC)),
            LTIBairro: index.BAIRRO_LOC.slice(0, 25),
            LTICidade: index.CIDADE_LOC.slice(0, 30),
            LTIUF: index.UF_LOC.slice(0, 2),
            distancia: Number(onlyNumbers(index.DISTANCIA)),
            valorVTA: Number(index['VALOR ANTES']?.replaceAll(',', '.')),
            valorVTI: Number(index['VALOR DEPOIS']?.replaceAll(',', '.')),
            codSindicato: Number(index.SIND),
            descrCargo: index.CARGO.slice(0, 40),
            raio: Number(index.RAIO),
          })));
        },
      });
    }
  }

  const handleSubmitFile = useCallback(async () => {
    const DestinyWorkplaceByCpf = csvParsed.map((index) => `${index.LTICod}-${index.cpf}`);
    if (hasDuplicates(DestinyWorkplaceByCpf)) {
      toast.error('Arquivo inválido (foram encontrados 2 ou mais cpfs para o mesmo local indicado - CODIGO_LOC + CPF_FUNC)');
      return;
    }
    await apiCall({
      apiToCall: TriangulationService.uploadTriangulationData,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      reqBody: JSON.stringify({
        dados: csvParsed,
      }),
      onStartLoad: () => setIsSubmitting(true),
      onEndLoad: () => setIsSubmitting(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar o upload do arquivo (${response.msg})`);
          return;
        }
        setIsModalVisible(false);
        toast.success('Arquivo importado com sucesso!');
        getTriangulationData();
      },
      catchMessage: 'Não foi possível importar o arquivo',
    });
  }, [apiCall, companyCode, csvParsed, getTriangulationData, userCode, userProfile]);

  return {
    isSubmitting,
    isModalVisible,
    setIsModalVisible,
    csvFileName,
    csvParsed,
    setCsvParsed,
    handleFileUpload,
    handleSubmitFile,
    setCsvFileName,
  };
}
